import * as RD from '@radix-ui/react-dialog';
import type { Session } from '@orgus/protocol';
import { MISSION_STATE_LABEL, statusLabel } from '@orgus/protocol';
import { CornerDownLeft, FolderGit2, Server, Settings, Target, Workflow } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Command as Cmdk, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Kbd, StatusDot, cn, formatRelative, plural, sessionTone, useFocusReturn } from '@orgus/ui';
import { closePalette, useCommandStore, useCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { useOrgus } from '../../lib/store.ts';
import { StepUpHost } from '../../features/act/StepUpHost.tsx';
import { useMissionsState } from '../../features/missions/data.ts';
import { missionsSlice, viewOf } from '../../features/missions/model.ts';
import { KindIcon } from '../../features/missions/parts.tsx';
import { itemValue, topMatches } from '../../features/palette/match.ts';
import { ActPreview, PowerGroups } from '../../features/palette/PowerGroups.tsx';
import { PALETTE_OWNS, SHADOWED_BY, usePaletteCommands } from '../../features/palette/usePaletteCommands.ts';
import { usePower } from '../../features/palette/usePower.ts';
import { SETTINGS_SECTIONS } from '../../features/settings/registry.ts';

/** Group order with an empty query; with a query, cmdk puts the best match first. */
const GROUP_ORDER = ['Actions', 'Recent sessions', 'Navigation', 'General'];
const MAX_ENTITIES = 8;
const MAX_FOUND = 6;
const RECENT_EMPTY = 5;

/**
 * Sessions for the palette: with a query, only those whose title has every word of it
 * (a project or machine name alone does not pull in its sessions, so the project itself
 * stays on top); without one, the most recent. At most MAX_ENTITIES either way.
 */
export function paletteSessions<T extends { title: string; status: string; lastActivity: number }>(all: T[], query: string): T[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return all
    .filter((s) => words.every((w) => s.title.toLowerCase().includes(w)))
    .sort((a, b) => Number(a.status === 'ended') - Number(b.status === 'ended') || b.lastActivity - a.lastActivity)
    .slice(0, MAX_ENTITIES);
}

function Item({
  value,
  keywords,
  onSelect,
  icon: Icon,
  leading,
  title,
  subtitle,
  shortcut,
  shortcutThen,
}: {
  value: string;
  keywords?: string[];
  onSelect: () => void;
  icon?: ComponentType<{ className?: string }>;
  leading?: ReactNode;
  title: string;
  subtitle?: string;
  shortcut?: string[];
  shortcutThen?: boolean;
}) {
  return (
    <CommandItem value={value} keywords={keywords} onSelect={onSelect} className="group gap-3 text-fg-muted">
      <span className="flex size-4 shrink-0 items-center justify-center text-fg-subtle group-data-[selected=true]:text-fg-muted">
        {leading ?? (Icon ? <Icon className="size-4" /> : null)}
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {/* The title keeps its width; the subtitle gets what is left, so a narrow palette cuts the subtitle first. */}
        <span className="truncate text-fg">{title}</span>
        {subtitle && <span className="min-w-0 flex-1 truncate text-caption text-fg-subtle">{subtitle}</span>}
      </span>
      {shortcut && <Kbd keys={shortcut} then={shortcutThen} />}
      <CornerDownLeft className="hidden size-4 text-fg-subtle group-data-[selected=true]:block" aria-hidden />
    </CommandItem>
  );
}

/** While the palette is open: on demo data, make sure the demo backlog exists so items and missions can be found. */
function MissionsData() {
  useMissionsState();
  return null;
}

const NO_SESSIONS: Record<string, Session> = {};
const NO_EXT: Record<string, unknown> = {};

/** Ctrl/Cmd+K: every navigation and action, plus backlog items, missions, settings, sessions, projects and machines by name. */
export function CommandPalette() {
  usePaletteCommands();
  const open = useCommandStore((s) => s.paletteOpen);
  const initialQuery = useCommandStore((s) => s.paletteQuery);
  const all = useCommands();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [lastOpen, setLastOpen] = useState(false);
  // Opened by a shortcut, not a trigger: give focus back to where it was on close.
  const focusReturn = useFocusReturn(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setQuery(initialQuery);
  }

  const commandTitles = useMemo(() => all.map((c) => c.title), [all]);
  const power = usePower(query, open, commandTitles);
  const text = power.mode === 'mixed' ? power.text : '';
  // Only read while open: at fleet scale every update would otherwise re-render the closed palette.
  const sessions = useOrgus((s) => (open ? s.sessions : NO_SESSIONS));
  const ext = useOrgus((s) => (open ? s.ext : NO_EXT));
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const projectName = (id: string | null | undefined) => (id ? projects[id]?.name : undefined);

  const commands = useMemo(() => {
    const ids = new Set(all.map((c) => c.id));
    return all.filter((c) => !PALETTE_OWNS.some((p) => c.id.startsWith(p)) && !(SHADOWED_BY[c.id] && ids.has(SHADOWED_BY[c.id]!)));
  }, [all]);

  const grouped = useMemo(() => {
    const g = new Map<string, Command[]>();
    for (const c of commands) {
      const k = c.group ?? 'General';
      g.set(k, [...(g.get(k) ?? []), c]);
    }
    const rank = (k: string) => GROUP_ORDER.indexOf(k) + 1 || (k === 'General' ? 99 : 2.5);
    return [...g.entries()].sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
  }, [commands]);

  const missions = useMemo(() => missionsSlice(ext), [ext]);
  const items = useMemo(
    () => (open && missions ? topMatches(missions.items.filter((i) => i.status !== 'dropped'), text, (i) => ({ title: `${i.key} ${i.title}`, keywords: [i.kind] }), MAX_FOUND) : []),
    [open, missions, text],
  );
  /** The few missions that match, each with its live state (derived from its sessions, as on the Missions page). */
  const foundMissions = useMemo(() => {
    if (!open || !missions) return [];
    const src = useOrgus.getState();
    const now = Date.now();
    return topMatches(missions.missions, text, (m) => ({ title: m.title, keywords: ['mission', m.id] }), MAX_FOUND).map((m) => ({ ...m, state: viewOf(m, src, now).state }));
  }, [open, missions, text]);
  const settings = useMemo(() => (open ? topMatches(SETTINGS_SECTIONS, text, (s) => ({ title: s.title, keywords: ['settings', ...(s.keywords ?? [])] }), MAX_FOUND) : []), [open, text]);

  const sessionList = useMemo(() => {
    if (!open) return [];
    const list = paletteSessions(Object.values(sessions), text.trim());
    return text.trim() ? list : list.slice(0, RECENT_EMPTY);
  }, [open, sessions, text]);
  const pipelineKeys = useOrgus((s) => s.pipelines);
  /** One "Open <project> pipeline" item per project that has a pipeline. */
  const projectPipelines = useMemo(() => {
    const out = new Map<string, string>();
    for (const [key, p] of Object.entries(pipelineKeys)) if (!out.has(p.projectId)) out.set(p.projectId, key);
    return out;
  }, [pipelineKeys]);

  const run = (fn: () => void | Promise<void>) => {
    closePalette();
    // Let the dialog close before the command moves focus or opens another dialog.
    setTimeout(() => void fn(), 0);
  };

  const searching = !!text.trim();
  const live = Object.values(sessions).filter((s) => s.status !== 'ended').length;

  return (
    <>
      <StepUpHost />
      {open && <MissionsData />}
      <RD.Root open={open} onOpenChange={(v) => (v ? undefined : closePalette())}>
        <RD.Portal>
          <RD.Overlay className="fixed inset-0 z-(--z-overlay) animate-overlay-in bg-backdrop" />
          <RD.Content
            aria-describedby={undefined}
            onOpenAutoFocus={focusReturn.onOpenAutoFocus}
            onCloseAutoFocus={focusReturn.onCloseAutoFocus}
            className="fixed inset-x-4 top-24 z-(--z-overlay) mx-auto max-w-160 animate-pop-in overflow-hidden rounded-overlay border border-border-strong bg-raised shadow-overlay outline-none"
          >
            <RD.Title className="sr-only">Command palette</RD.Title>
            <Cmdk label="Command palette" loop className="rounded-overlay bg-raised" filter={power.filter} onKeyDown={power.onKeyDown}>
              <div className="relative">
                <CommandInput
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Search items, missions, sessions, settings"
                  className="pr-12"
                />
                <RD.Close className="absolute right-3 top-1/2 -translate-y-1/2 rounded-control transition-opacity hover:opacity-80">
                  <span className="sr-only">Close</span>
                  <Kbd keys={['esc']} />
                </RD.Close>
              </div>
              <CommandList className="max-h-110 overscroll-contain p-1">
                {!power.answer && !power.acts.length && power.mode !== 'ask' && (
                  <CommandEmpty className="flex flex-col items-center gap-1 px-4 py-12 text-center">
                    <span className="text-ui font-medium text-fg">Nothing matches</span>
                    <span className="text-caption text-fg-subtle">Try an item key, a mission or session title, or a setting. Start with ? to ask, or &gt; for actions.</span>
                  </CommandEmpty>
                )}
                <PowerGroups power={power} run={run} onExample={setQuery} />

                {grouped
                  .filter(([group]) => group === 'Actions')
                  .map(([group, cmds]) => (
                    <CommandGroup key={group} heading={group}>
                      {cmds.map((c) => (
                        <Item
                          key={c.id}
                          value={itemValue(c.title, c.id)}
                          keywords={[...(c.keywords ?? []), c.subtitle ?? '']}
                          onSelect={() => run(c.perform)}
                          icon={c.icon}
                          title={c.title}
                          subtitle={c.subtitle}
                          shortcut={c.shortcut}
                          shortcutThen={c.shortcutThen}
                        />
                      ))}
                    </CommandGroup>
                  ))}

                {items.length > 0 && (
                  <CommandGroup heading="Backlog items">
                    {items.map((i) => (
                      <Item
                        key={i.id}
                        value={itemValue(`${i.key} ${i.title}`, `item:${i.id}`)}
                        keywords={[i.kind]}
                        onSelect={() => run(() => navigate(`/missions?item=${encodeURIComponent(i.id)}`))}
                        leading={<KindIcon kind={i.kind} tooltip={false} />}
                        title={`${i.key} ${i.title}`}
                        subtitle={[statusLabel(i.kind, i.status), projectName(i.projectId)].filter(Boolean).join(', ')}
                      />
                    ))}
                  </CommandGroup>
                )}

                {foundMissions.length > 0 && (
                  <CommandGroup heading="Missions">
                    {foundMissions.map((m) => (
                      <Item
                        key={m.id}
                        value={itemValue(m.title, `mission:${m.id}`)}
                        keywords={['mission', m.id]}
                        onSelect={() => run(() => navigate(`/missions/${encodeURIComponent(m.id)}`))}
                        icon={Target}
                        title={m.title}
                        subtitle={[MISSION_STATE_LABEL[m.state], projectName(m.projectId)].filter(Boolean).join(', ')}
                      />
                    ))}
                  </CommandGroup>
                )}

                {sessionList.length > 0 && (
                  <CommandGroup heading="Recent sessions">
                    {sessionList.map((s) => (
                      <Item
                        key={s.id}
                        value={itemValue(s.title, `session:${s.id}`)}
                        onSelect={() => run(() => navigate(`/sessions?session=${encodeURIComponent(s.id)}`))}
                        leading={<StatusDot tone={sessionTone(s)} size="sm" />}
                        title={s.title}
                        subtitle={`${projectName(s.projectId) ?? machines[s.machineId]?.name ?? ''}, ${formatRelative(s.lastActivity)}`.replace(/^, /, '')}
                      />
                    ))}
                  </CommandGroup>
                )}

                {/* A matching settings section is more specific than "Go to Settings", so it comes first. */}
                {settings.length > 0 && (
                  <CommandGroup heading="Settings">
                    {settings.map((s) => (
                      <Item
                        key={s.id}
                        value={itemValue(s.title, `settings:${s.id}`)}
                        keywords={['settings', ...(s.keywords ?? [])]}
                        onSelect={() => run(() => navigate(`/settings/${s.id}`))}
                        icon={s.icon ?? Settings}
                        title={s.title}
                        subtitle="Settings"
                      />
                    ))}
                  </CommandGroup>
                )}

                {grouped
                  .filter(([group]) => group !== 'Actions' && (group !== 'General' || searching))
                  .map(([group, cmds]) => (
                    <CommandGroup key={group} heading={group}>
                      {cmds.map((c) => (
                        <Item
                          key={c.id}
                          value={itemValue(c.title, c.id)}
                          keywords={[...(c.keywords ?? []), c.subtitle ?? '']}
                          onSelect={() => run(c.perform)}
                          icon={c.icon}
                          title={c.title}
                          subtitle={c.subtitle}
                          shortcut={c.shortcut}
                          shortcutThen={c.shortcutThen}
                        />
                      ))}
                    </CommandGroup>
                  ))}

                {searching && Object.keys(projects).length > 0 && (
                  <CommandGroup heading="Projects">
                    {Object.values(projects).map((p) => (
                      <Item
                        key={p.id}
                        value={itemValue(p.name, `project:${p.id}`)}
                        keywords={['project', p.remoteUrl ?? '']}
                        onSelect={() => run(() => navigate(`/projects?project=${encodeURIComponent(p.id)}`))}
                        icon={FolderGit2}
                        title={p.name}
                      />
                    ))}
                    {Object.values(projects)
                      .filter((p) => projectPipelines.has(p.id))
                      .map((p) => (
                        <Item
                          key={`pl-${p.id}`}
                          value={itemValue(`Open ${p.name} pipeline`, `pipeline:${p.id}`)}
                          onSelect={() => run(() => navigate(`/pipelines?pipeline=${encodeURIComponent(projectPipelines.get(p.id)!)}`))}
                          icon={Workflow}
                          title={`Open ${p.name} pipeline`}
                        />
                      ))}
                  </CommandGroup>
                )}

                {searching && Object.keys(machines).length > 0 && (
                  <CommandGroup heading="Machines">
                    {Object.values(machines).map((m) => (
                      <Item
                        key={m.id}
                        value={itemValue(m.name, `machine:${m.id}`)}
                        keywords={['machine', m.hostname, m.os]}
                        onSelect={() => run(() => navigate(`/machines?machine=${encodeURIComponent(m.id)}`))}
                        icon={Server}
                        title={m.name}
                        subtitle={m.online ? 'Online' : 'Offline'}
                      />
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
              <ActPreview power={power} />
              <div className={cn('flex h-control items-center gap-4 border-t border-border-subtle px-4 text-caption text-fg-subtle')}>
                <span className="flex items-center gap-1">
                  <Kbd keys={['up']} />
                  <Kbd keys={['down']} />
                  Move
                </span>
                <span className="flex items-center gap-1">
                  <Kbd keys={['enter']} />
                  Open
                </span>
                <span className="flex items-center gap-1">
                  <Kbd keys={['?']} />
                  Ask
                </span>
                <span className="ml-auto num">{plural(live, 'live session')}</span>
              </div>
            </Cmdk>
          </RD.Content>
        </RD.Portal>
      </RD.Root>
    </>
  );
}
